import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

export function AmbientSound() {
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = new Audio("/audio/bgm.mp3");
    audio.loop = true;
    audio.volume = 0.4;
    audioRef.current = audio;
    setReady(true);

    return () => {
      audio.pause();
      audio.src = "";
    };
  }, []);

  const toggle = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!playing) {
      try {
        await audio.play();
        setPlaying(true);
      } catch {
        // Autoplay blocked — user gesture required (already handled via button click)
      }
    } else {
      audio.pause();
      setPlaying(false);
    }
  };

  if (!ready) return null;

  return (
    <motion.button
      onClick={toggle}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 2 }}
      title={playing ? "Mute BGM" : "Play BGM"}
      className="fixed bottom-6 left-6 z-50 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300"
      style={{
        background: "oklch(0.12 0.01 240 / 0.8)",
        border: playing
          ? "1px solid oklch(0.75 0.20 195 / 0.6)"
          : "1px solid oklch(0.35 0.02 240 / 0.5)",
        boxShadow: playing
          ? "0 0 12px oklch(0.65 0.18 195 / 0.4), inset 0 0 8px oklch(0.55 0.15 195 / 0.2)"
          : "none",
        backdropFilter: "blur(8px)",
      }}
    >
      <AnimatePresence mode="wait">
        {playing ? (
          <motion.svg
            key="playing"
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.7, opacity: 0 }}
            transition={{ duration: 0.2 }}
            width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            style={{ color: "oklch(0.85 0.18 195)" }}
          >
            {/* Volume / music note icon */}
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14" strokeLinecap="round" />
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" strokeLinecap="round" />
          </motion.svg>
        ) : (
          <motion.svg
            key="muted"
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.7, opacity: 0 }}
            transition={{ duration: 0.2 }}
            width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            style={{ color: "oklch(0.55 0.04 240)" }}
          >
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <line x1="23" y1="9" x2="17" y2="15" strokeLinecap="round" />
            <line x1="17" y1="9" x2="23" y2="15" strokeLinecap="round" />
          </motion.svg>
        )}
      </AnimatePresence>
    </motion.button>
  );
}
